import { useState } from "react";
import type { MCQConfig, MCQOption } from "../../lib/types";

type Props = {
  value: MCQConfig;
  onChange: (next: MCQConfig) => void;
  /** When true, existing option labels remain editable but keys are locked and reorder/remove is blocked. */
  locked?: boolean;
};

export const DEFAULT_MCQ_CONFIG: MCQConfig = {
  options: [
    { key: "option_1", label: "" },
    { key: "option_2", label: "" },
  ],
  allow_other: true,
  other_label: "Sonstiges",
  why_prompt: "Warum diese Wahl?",
  why_required_for_other: true,
  why_for_all: false,
  multi_select: false,
};

function slug(label: string, fallback: string): string {
  const s = label
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[^\p{Letter}\p{Number}]+/gu, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 32);
  return s || fallback;
}

export function MCQConfigForm({ value, onChange, locked }: Props) {
  const [newLabel, setNewLabel] = useState("");

  function update<K extends keyof MCQConfig>(key: K, v: MCQConfig[K]) {
    onChange({ ...value, [key]: v });
  }

  function updateOption(idx: number, next: MCQOption) {
    const opts = value.options.map((o, i) => (i === idx ? next : o));
    onChange({ ...value, options: opts });
  }

  function addOption() {
    if (!newLabel.trim()) return;
    const key = slug(newLabel, `option_${value.options.length + 1}`);
    const dedupedKey = value.options.some((o) => o.key === key) ? `${key}_${value.options.length + 1}` : key;
    onChange({ ...value, options: [...value.options, { key: dedupedKey, label: newLabel.trim() }] });
    setNewLabel("");
  }

  function removeOption(idx: number) {
    if (locked) return;
    onChange({ ...value, options: value.options.filter((_, i) => i !== idx) });
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-2">
        <div className="text-sm font-medium text-rc-ink">Optionen</div>
        {value.options.map((opt, idx) => (
          <div key={opt.key} className="flex items-center gap-2">
            <input
              value={opt.label}
              onChange={(e) => updateOption(idx, { ...opt, label: e.target.value })}
              className="flex-1 rounded-lg border border-rc-ink/10 bg-rc-paper p-2 text-rc-ink focus:outline-none focus:ring-2 focus:ring-rc-berry"
              placeholder="Label"
            />
            <code className="text-xs text-rc-ink/40">{opt.key}</code>
            <button
              type="button"
              onClick={() => removeOption(idx)}
              disabled={locked}
              className="text-sm text-rc-ink/60 hover:text-rc-berry disabled:opacity-40"
              aria-label={`Option ${opt.label} entfernen`}
            >
              ✕
            </button>
          </div>
        ))}
        <div className="flex gap-2">
          <input
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            placeholder="Neue Option"
            className="flex-1 rounded-lg border border-rc-ink/10 bg-rc-paper p-2 text-rc-ink focus:outline-none focus:ring-2 focus:ring-rc-berry"
          />
          <button
            type="button"
            onClick={addOption}
            className="rounded-lg bg-rc-ink px-3 py-2 text-sm text-rc-cream"
          >
            + Hinzufügen
          </button>
        </div>
        {value.options.length > 7 && (
          <p className="text-xs text-rc-ink/60">
            Auf kleinen Bildschirmen werden lange Listen unübersichtlich.
          </p>
        )}
      </div>

      <label className="flex items-center gap-2 text-sm text-rc-ink/80">
        <input
          type="checkbox"
          checked={value.allow_other}
          onChange={(e) => update("allow_other", e.target.checked)}
        />
        „Sonstiges" zulassen
      </label>
      {value.allow_other && (
        <>
          <label className="flex flex-col gap-1">
            <span className="text-sm text-rc-ink/80">Label für „Sonstiges"</span>
            <input
              value={value.other_label}
              onChange={(e) => update("other_label", e.target.value)}
              className="rounded-lg border border-rc-ink/10 bg-rc-paper p-2 text-rc-ink focus:outline-none focus:ring-2 focus:ring-rc-berry"
            />
          </label>
          <label className="flex items-center gap-2 text-sm text-rc-ink/80">
            <input
              type="checkbox"
              checked={value.why_required_for_other}
              onChange={(e) => update("why_required_for_other", e.target.checked)}
            />
            Begründung für „Sonstiges" verpflichtend
          </label>
        </>
      )}

      <label className={`flex items-center gap-2 text-sm ${value.multi_select ? "text-rc-ink/40" : "text-rc-ink/80"}`}>
        <input
          type="checkbox"
          checked={!value.multi_select && value.why_for_all}
          disabled={value.multi_select}
          onChange={(e) => update("why_for_all", e.target.checked)}
        />
        Begründung für alle Optionen anbieten
        {value.multi_select && <span className="text-xs">(nur bei Einfachauswahl)</span>}
      </label>

      <label className="flex flex-col gap-1">
        <span className="text-sm text-rc-ink/80">Begründungs-Frage</span>
        <input
          value={value.why_prompt}
          onChange={(e) => update("why_prompt", e.target.value)}
          className="rounded-lg border border-rc-ink/10 bg-rc-paper p-2 text-rc-ink focus:outline-none focus:ring-2 focus:ring-rc-berry"
        />
      </label>

      <label className="flex items-center gap-2 text-sm text-rc-ink/80">
        <input
          type="checkbox"
          checked={value.multi_select}
          onChange={(e) => update("multi_select", e.target.checked)}
        />
        Mehrfachauswahl zulassen
      </label>

      {locked && (
        <p className="text-xs text-rc-ink/60">
          Diese Frage hat bereits Antworten – Reihenfolge und Entfernen vorhandener Optionen sind gesperrt. Sie können neue Optionen hinzufügen.
        </p>
      )}
    </div>
  );
}
